import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { BookCover } from "../components/BookCover";
import { StarDisplay } from "../components/StarRating";
import { Search, Globe, BookOpen, Library } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { SeriesAddModal } from "../components/SeriesAddModal";
import { getStoredLang } from "../components/LanguagePicker";
import { AdSenseBanner } from "../components/AdSenseBanner";
import { t, useLang } from "../lib/i18n";
import { Spinner } from "../components/Spinner";

const GENRES = ["all", "Fiction", "Non-Fiction", "Fantasy", "Sci-Fi", "Mystery", "Romance", "Biography", "History", "Self-Help", "Poetry", "Young Adult", "Children's"];
const AGES = ["all", "All Ages", "12+", "16+", "18+"];

const GENRE_MAP = [
  ["fantasy", "Fantasy"], ["science fiction", "Sci-Fi"], ["mystery", "Mystery"],
  ["thriller", "Mystery"], ["romance", "Romance"], ["biography", "Biography"],
  ["history", "History"], ["self-help", "Self-Help"], ["poetry", "Poetry"],
  ["young adult", "Young Adult"], ["juvenile", "Children's"], ["children", "Children's"],
  ["non-fiction", "Non-Fiction"], ["nonfiction", "Non-Fiction"], ["fiction", "Fiction"],
];

function inferGenre(subjects) {
  const text = (subjects || []).join(" ").toLowerCase();
  for (const [needle, label] of GENRE_MAP) if (text.includes(needle)) return label;
  return "Fiction";
}

export default function DiscoverPage() {
  useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";

  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(initialQuery);
  const [genre, setGenre] = useState("all");
  const [ageRating, setAgeRating] = useState("all");
  const [sort, setSort] = useState("recent");

  // Sync URL <- q so the search query survives reload + share.
  useEffect(() => {
    const current = searchParams.get("q") || "";
    if (q !== current) {
      if (q.trim()) setSearchParams({ q: q.trim() }, { replace: true });
      else setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // If the URL ?q= changes from outside (e.g. nav from home page), pull it in.
  useEffect(() => {
    const incoming = searchParams.get("q") || "";
    if (incoming && incoming !== q) setQ(incoming);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("q")]);

  // Open Library global search state
  const [olResults, setOlResults] = useState([]);
  const [olLoading, setOlLoading] = useState(false);
  const [addingKey, setAddingKey] = useState("");
  const [knownKeys, setKnownKeys] = useState(new Set());

  const navigate = useNavigate();
  const { user } = useAuth();
  const isSearching = q.trim().length >= 2;
  const [seriesSeed, setSeriesSeed] = useState("");

  // Local library load (when not searching, or always to know which OL keys exist locally)
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    api.get("/books", { params: { genre, age_rating: ageRating, sort }, signal: ctrl.signal })
      .then(({ data }) => {
        setBooks(data);
        setKnownKeys(new Set(data.map((b) => b.openlibrary_key).filter(Boolean)));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [genre, ageRating, sort]);

  // Open Library search when user types
  const [authorResults, setAuthorResults] = useState([]);
  const [trending, setTrending] = useState([]); // fallback when local library is sparse
  useEffect(() => {
    if (isSearching) return; // only load when not actively searching
    const ctrl = new AbortController();
    const fields = "key,title,author_name,first_publish_year,cover_i,number_of_pages_median,subject,language";
    const lang = getStoredLang();
    fetch(`https://openlibrary.org/trending/weekly.json?limit=48&language=${lang}&fields=${fields}`, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((data) => {
        // Take everything with a cover; Open Library's `language` field is
        // frequently missing from the trending response even when the record
        // is genuinely English, so we don't filter on it client-side any
        // more (the server-side `language=` query above already narrows).
        const works = (data.works || []).filter((w) => w.cover_i);
        setTrending(works.slice(0, 24));
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, [isSearching]);
  useEffect(() => {
    if (!isSearching) { setOlResults([]); setAuthorResults([]); return; }
    const ctrl = new AbortController();
    setOlLoading(true);
    const t = setTimeout(async () => {
      try {
        const lang = getStoredLang();
        const bookParams = new URLSearchParams({
          q,
          language: lang,
          limit: "48",
          fields: "key,title,author_name,first_publish_year,cover_i,number_of_pages_median,subject,language",
        });
        const [booksRes, authorsRes] = await Promise.all([
          fetch(`https://openlibrary.org/search.json?${bookParams}`, { signal: ctrl.signal }).then((r) => r.json()).catch(() => ({ docs: [] })),
          fetch(`https://openlibrary.org/search/authors.json?q=${encodeURIComponent(q)}&limit=6`, { signal: ctrl.signal }).then((r) => r.json()).catch(() => ({ docs: [] })),
        ]);
        // Trust the server-side `language=` filter. Client-side re-filtering
        // on `d.language.includes(lang)` dropped almost every result because
        // Open Library often omits the `language` field from search hits
        // even when the record genuinely matches the requested language.
        const docs = booksRes.docs || [];
        setOlResults(docs.slice(0, 24));
        setAuthorResults((authorsRes.docs || []).filter((a) => a.key));
      } catch (e) {
        if (e.name !== "AbortError") { setOlResults([]); setAuthorResults([]); }
      } finally {
        setOlLoading(false);
      }
    }, 320);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q, isSearching]);

  const handleOpenOlBook = async (doc) => {
    if (!user || typeof user !== "object") {
      toast.message("Sign in to open or add this book.");
      navigate("/login", { state: { from: "/discover" } });
      return;
    }
    setAddingKey(doc.key || "");
    try {
      // Fetch description
      let description = "";
      try {
        const w = await fetch(`https://openlibrary.org${doc.key}.json`);
        const work = await w.json();
        if (typeof work.description === "string") description = work.description;
        else if (work.description?.value) description = work.description.value;
      } catch { /* ignore — description is optional */ }

      const payload = {
        title: doc.title || "",
        author: (doc.author_name && doc.author_name[0]) || "Unknown",
        cover_url: doc.cover_i ? `https://covers.openlibrary.org/b/id/${doc.cover_i}-L.jpg` : "",
        genre: inferGenre(doc.subject),
        age_rating: "All Ages",
        year: doc.first_publish_year || null,
        pages: doc.number_of_pages_median || null,
        description: description.split("---")[0].trim().slice(0, 1200),
        openlibrary_key: doc.key || "",
      };
      const { data } = await api.post("/books", payload);
      navigate(`/books/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAddingKey("");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 fade-up" data-testid="discover-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#525252] mb-3 inline-flex items-center gap-2">
            <Globe className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("discover.eyebrow")}
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-[#0A0A0A] tracking-tight">{t("discover.title")}</h1>
        </div>
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A3A3A3]" strokeWidth={1.5} />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t("home.search_placeholder")}
            className="w-full bg-[#FAF5E6] border border-[rgba(0,0,0,0.12)] rounded-sm pl-9 pr-10 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
            data-testid="discover-search"
          />
          {olLoading && <Spinner size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#525252]" />}
        </div>
      </div>

      <AdSenseBanner placement="discover-inline" />

      {isSearching ? (
        <>
          <AuthorResults authors={authorResults} />
          <OpenLibraryResults
            results={olResults}
            loading={olLoading}
            knownKeys={knownKeys}
            addingKey={addingKey}
            onOpen={handleOpenOlBook}
            onOpenSeries={user ? ((key) => setSeriesSeed(key)) : null}
          />
        </>
      ) : (
        <>
          {/* Only render the LocalLibrary block when the community has
              actually added something — otherwise the empty-state callout
              is confusing (Open Library is right there below). */}
          {(loading || books.length > 0) && (
            <LocalLibrary
              books={books} loading={loading}
              genre={genre} setGenre={setGenre}
              ageRating={ageRating} setAgeRating={setAgeRating}
              sort={sort} setSort={setSort}
            />
          )}
          {trending.length > 0 && (
            <TrendingFromOL
              works={trending}
              knownKeys={knownKeys}
              addingKey={addingKey}
              onOpen={handleOpenOlBook}
              primary={!loading && books.length === 0}
            />
          )}
        </>
      )}

      <SeriesAddModal
        open={!!seriesSeed}
        seedKey={seriesSeed}
        onClose={() => setSeriesSeed("")}
        onAdded={() => { setSeriesSeed(""); /* refresh known keys on next type */ }}
      />
    </div>
  );
}

function AuthorResults({ authors }) {
  if (!authors || authors.length === 0) return null;
  return (
    <section className="mb-12 pb-10 border-b border-[rgba(0,0,0,0.08)]" data-testid="author-results">
      <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-5">
        {authors.length} {authors.length === 1 ? "author" : "authors"}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-5">
        {authors.map((a) => {
          const olid = (a.key || "").replace("/authors/", "");
          return (
            <Link to={`/authors/${olid}`} key={a.key} className="group block" data-testid={`author-result-${olid}`}>
              <div className="aspect-square overflow-hidden border border-[rgba(0,0,0,0.10)] bg-[#E8DFC1]">
                <img
                  src={`https://covers.openlibrary.org/a/olid/${olid}-M.jpg?default=false`}
                  alt={a.name}
                  loading="lazy"
                  onError={(e) => {
                    const el = e.currentTarget;
                    el.style.display = "none";
                    if (el.parentElement) {
                      el.parentElement.innerHTML = `<div class='w-full h-full flex items-center justify-center font-heading text-3xl text-[#A3A3A3]'>${(a.name || "?")[0].toUpperCase()}</div>`;
                    }
                  }}
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                />
              </div>
              <div className="mt-2 font-heading text-sm text-[#0A0A0A] line-clamp-1 group-hover:text-[#525252] transition-colors">{a.name}</div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-[#A3A3A3]">{a.work_count || 0} works</div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function OpenLibraryResults({ results, loading, knownKeys, addingKey, onOpen, onOpenSeries }) {
  if (loading && results.length === 0) {
    return <div className="font-mono uppercase tracking-widest text-xs text-[#A3A3A3]" data-testid="ol-loading">Searching Open Library…</div>;
  }
  if (results.length === 0) {
    return (
      <div className="border border-dashed border-[rgba(0,0,0,0.12)] p-12 text-center">
        <div className="font-heading text-2xl text-[#525252]">No matches on Open Library</div>
        <div className="font-mono uppercase tracking-widest text-[10px] text-[#A3A3A3] mt-3">Try a different title or author</div>
      </div>
    );
  }
  return (
    <div data-testid="ol-results">
      <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-6">
        {results.length} {results.length === 1 ? "match" : "matches"} on Open Library
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-4 md:gap-5">
        {results.map((doc) => {
          const cover = doc.cover_i ? `https://covers.openlibrary.org/b/id/${doc.cover_i}-L.jpg` : "";
          const isKnown = knownKeys.has(doc.key);
          const isAdding = addingKey === doc.key;
          return (
            <div
              key={doc.key}
              className="group relative text-left"
              data-testid={`ol-result-${doc.key.replace(/[^a-zA-Z0-9]/g,'_')}`}
            >
              {onOpenSeries && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); onOpenSeries(doc.key); }}
                  className="absolute z-10 top-2 right-2 w-8 h-8 rounded-sm bg-[#F2EAD3]/95 border border-[rgba(10,10,10,0.18)] flex items-center justify-center text-[#0A0A0A] opacity-0 group-hover:opacity-100 hover:bg-[#FAF5E6] transition-opacity"
                  title="Add the whole series"
                  aria-label="Add the whole series"
                  data-testid={`ol-result-series-${doc.key.replace(/[^a-zA-Z0-9]/g,'_')}`}
                >
                  <Library className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              )}
              <button
                type="button"
                onClick={() => onOpen(doc)}
                disabled={isAdding}
                className="block w-full text-left disabled:opacity-50"
              >
                <BookCover src={cover} title={doc.title || ""} author={(doc.author_name && doc.author_name[0]) || ""} />
                <div className="mt-2.5 space-y-1">
                  <div className="font-heading text-sm text-[#0A0A0A] leading-tight line-clamp-2 group-hover:text-[#525252] transition-colors">{doc.title}</div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-[#525252] line-clamp-1">
                    {(doc.author_name && doc.author_name[0]) || "Unknown author"}
                    {doc.first_publish_year ? ` · ${doc.first_publish_year}` : ""}
                  </div>
                  <div className="pt-1 font-mono text-[9px] uppercase tracking-widest">
                    {isAdding ? <span className="text-[#525252]">Opening…</span>
                      : isKnown ? <span className="text-[#0A0A0A] inline-flex items-center gap-1"><BookOpen className="w-3 h-3" strokeWidth={1.5} /> In library</span>
                      : <span className="text-[#525252]">Click to add</span>}
                  </div>
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LocalLibrary({ books, loading, genre, setGenre, ageRating, setAgeRating, sort, setSort }) {
  const translateOption = (o) => {
    // Map filter option values to i18n keys.
    if (o === "all") return t("discover.all");
    if (o === "recent") return t("discover.recent");
    if (o === "top") return t("discover.top_rated");
    if (o === "title") return t("discover.title_sort");
    if (o === "All Ages") return t("age.all_ages");
    if (o === "12+") return t("age.12_plus");
    if (o === "16+") return t("age.16_plus");
    if (o === "18+") return t("age.18_plus");
    // For genre list, fall through — translation done in i18n if provided.
    const key = `genre.${o.toLowerCase().replace(/\s+/g, "_")}`;
    const translated = t(key);
    return translated === key ? o : translated;
  };
  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-10 pb-6 border-b border-[rgba(0,0,0,0.08)]">
        <FilterSelect label={t("discover.genre_label")} value={genre} onChange={setGenre} options={GENRES} testId="filter-genre" translate={translateOption} />
        <FilterSelect label={t("discover.age_label")} value={ageRating} onChange={setAgeRating} options={AGES} testId="filter-age" translate={translateOption} />
        <FilterSelect label={t("discover.sort_label")} value={sort} onChange={setSort} options={["recent", "top", "title"]} testId="filter-sort" translate={translateOption} />
        <div className="ml-auto font-mono uppercase tracking-widest text-[10px] text-[#525252]" data-testid="book-count">
          {books.length} {t("discover.in_library")}
        </div>
      </div>

      {loading ? (
        <div className="font-mono uppercase tracking-widest text-xs text-[#A3A3A3]">{t("common.loading")}</div>
      ) : books.length === 0 ? (
        <div className="border border-dashed border-[rgba(0,0,0,0.12)] p-16 text-center">
          <div className="font-heading text-2xl text-[#525252]">{t("discover.empty")}</div>
          <div className="font-mono uppercase tracking-widest text-[10px] text-[#A3A3A3] mt-3">{t("discover.empty_hint")}</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-4 md:gap-5">
          {books.map((b) => (
            <Link key={b.id} to={`/books/${b.id}`} className="group" data-testid={`book-card-${b.id}`}>
              <BookCover src={b.cover_url} title={b.title} author={b.author} />
              <div className="mt-2.5 space-y-1">
                <div className="font-heading text-sm text-[#0A0A0A] leading-tight line-clamp-2 group-hover:text-[#525252] transition-colors">{b.title}</div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-[#525252] line-clamp-1">{b.author}</div>
                <div className="flex items-center gap-2 pt-1">
                  <StarDisplay value={b.avg_rating} size={11} />
                  <span className="font-mono text-[9px] text-[#525252]">{b.avg_rating.toFixed(1)} · {b.review_count}</span>
                </div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-[#A3A3A3] line-clamp-1">{translateOption(b.genre)} · {translateOption(b.age_rating)}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

function TrendingFromOL({ works, knownKeys, addingKey, onOpen, primary }) {
  return (
    <section
      className={primary ? "" : "mt-16 pt-12 border-t border-[rgba(0,0,0,0.08)]"}
      data-testid="trending-from-ol"
    >
      <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#525252] mb-3">
        {primary ? "Browse Open Library" : "Trending on Open Library"}
      </div>
      <h2 className="font-heading text-2xl md:text-3xl text-[#0A0A0A] tracking-tight mb-8">
        {primary ? "Millions of books, one tap away" : "A fresh batch every week"}
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-4 md:gap-5">
        {works.slice(0, 24).map((w) => {
          const cover = w.cover_i ? `https://covers.openlibrary.org/b/id/${w.cover_i}-L.jpg` : "";
          const author = (w.author_name && w.author_name[0]) || "Unknown";
          const isKnown = knownKeys.has(w.key);
          return (
            <button key={w.key} onClick={() => onOpen(w)} disabled={addingKey === w.key} className="text-left group disabled:opacity-50" data-testid={`trending-${(w.key || "").replace(/\W/g, "_")}`}>
              <div className="aspect-[2/3] overflow-hidden border border-[rgba(0,0,0,0.10)] bg-[#E8DFC1]">
                {cover ? (
                  <img src={cover} alt={w.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-heading text-3xl text-[#A3A3A3]">{(w.title || "?")[0]}</div>
                )}
              </div>
              <div className="mt-2.5 space-y-1">
                <div className="font-heading text-sm text-[#0A0A0A] line-clamp-2 leading-tight group-hover:text-[#525252] transition-colors">{w.title}</div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-[#525252] line-clamp-1">{author}</div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-[#A3A3A3]">{isKnown ? "In community library" : "Click to add"}</div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}


function FilterSelect({ label, value, onChange, options, testId, translate }) {
  const render = translate || ((o) => (o === "all" ? "All" : o[0].toUpperCase() + o.slice(1)));
  return (
    <label className="flex items-center gap-2 text-xs">
      <span className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{label}</span>
      <select
        value={value} onChange={(e) => onChange(e.target.value)}
        className="bg-[#FAF5E6] border border-[rgba(0,0,0,0.08)] rounded-sm px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-black"
        data-testid={testId}
      >
        {options.map((o) => <option key={o} value={o}>{render(o)}</option>)}
      </select>
    </label>
  );
}
